import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all'); // all, approved, pending

  useEffect(() => {
    if (isConfigured) {
      fetchReviews();
    }
  }, []);

  const fetchReviews = async () => {
    if (!isConfigured) return;
    try {
      const querySnapshot = await getDocs(collection(db, 'reviews'));
      const reviewsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by newest first
      reviewsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReviews(reviewsList);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleApprove = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'reviews', id), {
        approved: !currentStatus,
        approvedAt: !currentStatus ? new Date().toISOString() : null
      });
      alert(`Review ${!currentStatus ? 'approved' : 'unapproved'} successfully!`);
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      alert('Error updating review. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteDoc(doc(db, 'reviews', id));
        alert('Review deleted successfully!');
        fetchReviews();
      } catch (error) {
        console.error('Error deleting review:', error);
        alert('Error deleting review. Please try again.');
      }
    }
  };

  const renderStars = (rating) => {
    const numStars = parseInt(rating) || 5;
    return (
      <div className='flex gap-1'>
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-5 h-5 ${
              index < numStars ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
            />
          </svg>
        ))}
      </div>
    );
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'approved') return review.approved === true;
    if (filter === 'pending') return review.approved === false || !review.approved;
    return true;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-7xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold'>⚠️ Firebase Not Configured</p>
            </div>
          )}

          {/* Header */}
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6'>
            <div>
              <h1 className='text-3xl font-bold text-white'>REVIEWS / TESTIMONIALS</h1>
              <p className='text-white text-sm mt-1'>Manage client reviews and testimonials</p>
            </div>
            <a
              href='/add-review'
              target='_blank'
              rel='noopener noreferrer'
              className='bg-white text-[#FFB7CE] px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-colors duration-200'
            >
              📝 Review Form Link
            </a>
          </div>

          {/* Filter Tabs */}
          <div className='bg-white rounded-lg p-4 mb-6 shadow-lg'>
            <div className='flex gap-2 flex-wrap'>
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-[#FFB7CE] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({reviews.length})
              </button>
              <button
                onClick={() => setFilter('approved')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'approved'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Approved ({reviews.filter(r => r.approved).length})
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'pending'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({reviews.filter(r => !r.approved).length})
              </button>
            </div>
          </div>

          {/* Reviews Grid */}
          {filteredReviews.length === 0 ? (
            <div className='bg-white rounded-lg p-12 text-center shadow-lg'>
              <svg className='w-16 h-16 mx-auto mb-4 text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' />
              </svg>
              <p className='text-gray-500 text-lg font-medium'>No reviews found</p>
              <p className='text-gray-400 text-sm mt-2'>
                {filter === 'pending' 
                  ? 'No pending reviews to approve'
                  : filter === 'approved'
                  ? 'No approved reviews yet'
                  : 'Share the review form link with your clients'}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {filteredReviews.map((review) => (
                <div
                  key={review.id}
                  className='bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-200'
                >
                  {/* Header */}
                  <div className='flex justify-between items-start mb-4'>
                    <div className='flex-1'>
                      <h3 className='font-bold text-lg text-gray-800'>{review.name}</h3>
                      <p className='text-sm text-gray-600'>{review.company}</p>
                      {review.projectTitle && (
                        <span className='inline-block mt-1 bg-[#FFB7CE] bg-opacity-10 text-[#FFB7CE] px-2 py-1 rounded text-xs font-medium'>
                          {review.projectTitle}
                        </span>
                      )}
                    </div>
                    <div className='flex flex-col items-end gap-2'>
                      {renderStars(review.ratings || review.rating)}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          review.approved
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {review.approved ? '✓ Approved' : '⏳ Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Review Text */}
                  <p className='text-gray-700 text-sm leading-relaxed mb-4'>
                    {review.review}
                  </p>

                  {/* Footer */}
                  <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-4 border-t border-gray-100'>
                    <span className='text-xs text-gray-500'>
                      {formatDate(review.createdAt)}
                    </span>
                    <div className='flex gap-2'>
                      <button
                        onClick={() => handleApprove(review.id, review.approved)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          review.approved
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {review.approved ? 'Unapprove' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleDelete(review.id)}
                        className='px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors'
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}